import { CircularLoader } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import ChangeIndication from "app/components/ChangeIndication/ChangeIndication";
import { TitleWithInfo } from "app/components/TitleWithInfo/TitleWithInfo";
import { translate } from "app/filters/i18nFilter";
import { MetricFormat } from "app/common/benchmark/types";
import { FORMATTERS, PERCENTAGE_POINT } from "app/common/benchmark/formatters";
import { getPeriodDatesFromDuration } from "app/pages/benchmark-tracker/helpers/dateHelpers";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import type { PrimaryEntityMetadata } from "app/pages/benchmark-tracker/types/api.types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { PeriodContainer } from "./styles";

type PeriodBlockSchema = {
    yoy: PrimaryEntityMetadata["yearOverYearShare"];
    pop: PrimaryEntityMetadata["periodOverPeriodShare"];
    isLoading: boolean;
};

type PeriodInfoSchema = {
    value: number;
    title: string;
    tooltip?: string;
    isLoading: boolean;
};

const PeriodInfo = ({ value, title, tooltip, isLoading }: PeriodInfoSchema) => {
    const formattedValue = FORMATTERS[MetricFormat.percent](value).toString().replace("-", "");

    return (
        <PeriodContainer>
            {isLoading ? (
                <CircularLoader isLarge />
            ) : (
                <>
                    <ChangeIndication
                        isNotChanged={value === 0}
                        isIncreasing={value > 0}
                        changeValue={`${formattedValue} ${PERCENTAGE_POINT}`}
                    />

                    <TitleWithInfo
                        title={title}
                        tooltip={tooltip}
                        size="medium"
                    />
                </>
            )}
        </PeriodContainer>
    );
};

export const PeriodBlock = ({ yoy, pop, isLoading }: PeriodBlockSchema) => {
    const { granularity, duration } = proNavigator.getParams();

    const isMonthly = granularity === BenchmarkGranularity.MONTHLY;

    const myYoYText = translate("bt.metrics.my.yoy.text");
    const myPoPText = isMonthly ? translate("bt.metrics.my.mom.text") : translate("bt.metrics.my.wow.text");

    const { yoy: yoyDates, mom: momDates, wow: wowDates } = getPeriodDatesFromDuration(duration);

    const yoyStartDate = `${yoyDates.month} ${yoyDates.year}`;
    const yoyFinalDate = `${yoyDates.month} ${yoyDates.toYear}`;

    const momStartDate = `${momDates.month} ${momDates.year}`;
    const momFinalDate = `${momDates.toMonth} ${momDates.toYear}`;

    const wowStartDate = `${wowDates.month} ${wowDates.day}, ${wowDates.year}`;
    const wowFinalDate = `${wowDates.toMonth} ${wowDates.toDay}, ${wowDates.toYear}`;

    const popStartDate = isMonthly ? momStartDate : wowStartDate;
    const popFinalDate = isMonthly ? momFinalDate : wowFinalDate;

    return (
        <Stack width="50%">
            <PeriodInfo
                title={myPoPText}
                tooltip={translate("bt.metrics.tooltip.pop.trafficShare", {
                    startDate: popStartDate,
                    finalDate: popFinalDate,
                })}
                value={pop}
                isLoading={isLoading}
            />

            <PeriodInfo
                title={myYoYText}
                tooltip={translate("bt.metrics.tooltip.yoy.trafficShare", {
                    startDate: yoyStartDate,
                    finalDate: yoyFinalDate,
                })}
                value={yoy}
                isLoading={isLoading}
            />
        </Stack>
    );
};
